// Angular
import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

// Libraries
import { Observable, takeUntil } from 'rxjs';
import { Select, Store } from '@ngxs/store';

// Services
import { SideNavService } from './services/side-nav.service';

// Models
import { AppCleanup } from '../../models';
import { MenuBadgeModel } from '../../config/menu.config';

// Store
import { StorageState, SideNavModel } from '../../state/storage.state';
import { StorageActions } from '../../state/storage.action';

// Helpers
import { UtilityHelper } from '../../helpers';
import { StatusType } from '../../enums';

@Component({
	selector: 'side-nav',
	templateUrl: './side-nav.component.html',
	styleUrl: './side-nav.component.scss',
})
export class SideNavComponent extends AppCleanup implements OnInit, OnDestroy {
	@Select(StorageState.sideNav) sideNav$: Observable<SideNavModel>;
	@Select(StorageState.currentRoute) currentRoute$: Observable<string>;
	sideNav: SideNavModel;
	opened: boolean = false;
	collapsed: boolean = false;
	pinned: boolean = false;
	accordionState: number = -1;
	menuList: any[];
	currentRoute = '';
	pendingUserCount: number;
	previousInnerWidth: number;

	constructor(
		private store: Store,
		private sideNavService: SideNavService,
		private changeDetectorRef: ChangeDetectorRef
	) {
		super();
	}

	@HostListener('window:resize', ['$event'])
	onWindowResize() {
		if (this.previousInnerWidth < 1200 && window.innerWidth > 1200) {
			this.open();
		} else if (window.innerWidth < 1200 && this.opened) {
			if (this.previousInnerWidth > 1200) {
				// If screen getting smaller
				this.collapsed = false;

				if (this.opened) {
					this.close();
				}
			}
		}

		this.previousInnerWidth = window.innerWidth;
	}

	ngOnInit() {
		this.currentRoute$.pipe(takeUntil(this.destroy)).subscribe((currentRoute) => {
			this.currentRoute = currentRoute;
		});

		this.sideNavService.menuList$.pipe(takeUntil(this.destroy)).subscribe((menuList) => {
			if (menuList) {
				this.menuList = menuList;
			}
		});

		let initialLoad = true; // This is needed for when the side nav is set to open on initial load, and the screen is small
		this.sideNav$.pipe(takeUntil(this.destroy)).subscribe((sideNav) => {
			if (sideNav) {
				this.sideNav = UtilityHelper.deepCopy(sideNav);
				this.opened = sideNav.opened;

				if (window.innerWidth > 1200) {
					this.collapsed = sideNav.collapsed;
					this.pinned = sideNav.pinned;
				} else {
					if (this.opened && initialLoad) {
						this.opened = false;
						this.close();
					}
				}
			} else {
				this.sideNav = new SideNavModel();
			}

			if (window.innerWidth > 1200 && !this.sideNav.opened) {
				this.open();
			}

			initialLoad = false;
		});
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	toggle() {
		this.sideNav.opened = !this.sideNav.opened;
		this.store.dispatch(new StorageActions.SetSideNav(this.sideNav));
	}

	collapseToggle() {
		this.sideNav.collapsed = !this.sideNav.collapsed;
		this.sideNav.pinned = !this.sideNav.pinned;
		this.store.dispatch(new StorageActions.SetSideNav(this.sideNav));
		let value = this.sideNav.collapsed;

		if (value) {
			this.collapseAccordions();
		}
	}

	open() {
		this.sideNav.opened = true;
		this.store.dispatch(new StorageActions.SetSideNav(this.sideNav));
		this.changeDetectorRef.detectChanges(); // Fixes NG0100 warning
	}

	close() {
		if (window.innerWidth <= 1200) {
			if (this.sideNav) {
				this.sideNav.opened = false;
				this.store.dispatch(new StorageActions.SetSideNav(this.sideNav));
			}
		}
		this.changeDetectorRef.detectChanges(); // Fixes NG0100 warning
	}

	collapseAccordions() {
		this.accordionState = -1; // Collapses accordion menu
	}

	mouseEnter() {
		// if (this.collapsed && window.innerWidth > 1200) {
		// 	this.sideNav.collapsed = false;
		// 	this.store.dispatch(new StorageActions.SetSideNav(this.sideNav));
		// }
	}

	mouseLeave() {
		// if (!this.collapsed && !this.pinned && window.innerWidth > 1200) {
		// 	this.sideNav.collapsed = true;
		// 	this.store.dispatch(new StorageActions.SetSideNav(this.sideNav));
		// 	this.collapseAccordions();
		// }
	}

	setAccordionState(state: number) {
		this.accordionState = state;
	}

	getItemClass(item) {
		let classes = '';

		if (item.bottom) {
			classes += ' mt-auto';
		}

		if (item.root && (this.isItemActive(item) || this.isSubitemOfItemActive(item))) {
			classes += ' active';
		}

		if (!item.root && this.isItemActive(item)) {
			classes += ' active';
		}

		return classes;
	}

	isItemActive(item): boolean {
		if (!item.page) {
			return false;
		}

		if (this.currentRoute.includes('/job')) {
			// Since job routes are conflicting, there needs to be a custom approach
			if (this.currentRoute === '/job' && item.page === '/job') {
				return true;
			} else if (this.currentRoute === '/job/complete' && item.page === '/job/complete') {
				return true;
			}

			return false;
		} else if (this.currentRoute.includes('/asset') && item.page === '/unit') {
			// Since unit routes are conflicting, there needs to be a custom approach
			return false;
		} else {
			return this.currentRoute === item.page || this.currentRoute.includes(item.page);
		}
	}

	isSubitemOfItemActive(item): boolean {
		let result = false;

		if (item.submenu && item.submenu.length > 0) {
			for (const subItem of item.submenu) {
				if (this.currentRoute === subItem.page) {
					result = true;
				}
			}
		}
		return result;
	}

	isSubitemActive(subitem) {
		let result = false;

		if (this.currentRoute === subitem.page || this.currentRoute.includes(subitem.page)) {
			result = true;
		}

		return result;
	}

	isBottom(item) {
		return item.bottom;
	}

	getBadgeClass(badge: MenuBadgeModel) {
		let elementClass: string;

		if (badge?.type === StatusType.SUCCESS) {
			elementClass = 'success';
		} else if (badge?.type === StatusType.DANGER) {
			elementClass = 'danger';
		} else if (badge?.type === StatusType.WARNING) {
			elementClass = 'warning';
		}

		return elementClass;
	}
}
